import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatDateOrNA } from '../utils/formatDate';
import axios from 'axios';
import EditInsuranceDialog from './components/EditInsuranceDialog';
import RequestInsurancePolicyDialog from './components/RequestInsurancePolicyDialog';
// Assuming an AddPolicyDialog exists or will be created.
// If reusing PolicyFormDialog from submissions, import that instead.
// import AddPolicyDialog from './components/AddPolicyDialog';
import ConfirmationDialog from './components/ConfirmationDialog'; // Import ConfirmationDialog
import { useNotification } from '../context/NotificationContext';
import {
  Typography,
  Box,
  Paper,
  Divider,
  Button,
  CircularProgress,
  TextField,
  Grid,
  InputAdornment,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination, // Adding global pagination
  Chip,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  FilterAlt as FilterIcon,
  Add as AddIcon, // Add Icon
  Delete as DeleteIcon // Delete Icon
} from '@mui/icons-material';
import { getDocumentUrl, hasPermission } from '../utils/authUtils';
// Assuming PolicyFormDialog might be reused from submissions
import PolicyFormDialog from '../components/Submissions/PolicyFormDialog';

// PDF View Modal Component
const ViewCOIModal = ({ open, onClose, documentUrl }) => {
  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>View Certificate of Insurance</DialogTitle>
      <DialogContent>
        {documentUrl ? (
          <iframe
            src={documentUrl}
            style={{ width: '100%', height: '75vh', border: 'none' }}
            title="COI Document"
          />
        ) : (
          <Typography>No document URL provided or document is loading...</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const DEFAULT_POLICY_TYPES = [
  'Commercial General Liability',
  'Commercial Auto Liability',
  'Cargo Insurance',
  'On-Hook Coverage',
  'Garagekeepers Legal Liability',
  "Workers' Compensation",
  'Professional Liability',
  'Business Interruption Insurance',
  'Other'
];
const DEFAULT_STATUSES = ['Active', 'Expired', 'Pending', 'Rejected', 'Needs Review'];
const EMPTY_FILTER_OPTIONS = {
  vendors: [], providers: [], contactNames: [], contactEmails: [], countries: [], cities: [], states: [], zipCodes: [], policyTypes: [], statuses: []
};

function InsuranceSettings() {
  const { showNotification } = useNotification();
  const [groupedPolicies, setGroupedPolicies] = useState(new Map()); // Changed state name and type
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0); // Adding global pagination
  const [rowsPerPage, setRowsPerPage] = useState(10); // Adding global pagination
  const [totalCount, setTotalCount] = useState(0); // Adding global pagination
  const [editDialogOpen, setEditDialogOpen] = useState(false); // Edit Dialog state
  const [selectedInsurance, setSelectedInsurance] = useState(null); // Policy being edited
  const [addDialogOpen, setAddDialogOpen] = useState(false); // Add Dialog state
  const [currentVendorForAdd, setCurrentVendorForAdd] = useState(null); // { vendorIdentifier, companyName } context for Add Dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false); // Delete Confirmation Dialog state
  const [policyToDelete, setPolicyToDelete] = useState(null); // Policy marked for deletion
  const [isDeleting, setIsDeleting] = useState(false); // Loading state for delete operation
  const [viewCOIOpen, setViewCOIOpen] = useState(false);
  const [coiToViewUrl, setCoiToViewUrl] = useState('');
  const [requestPolicyDialogOpen, setRequestPolicyDialogOpen] = useState(false); // Request Policy Dialog state

  // Filter options come from the insurance records themselves and are
  // narrowed by the current vendor / provider selection
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  const policyTypeOptions = filterOptions.policyTypes?.length ? filterOptions.policyTypes : DEFAULT_POLICY_TYPES;
  const statusOptions = filterOptions.statuses?.length ? filterOptions.statuses : DEFAULT_STATUSES;
  const vendorOptions = [{ vendorIdentifier: null, companyName: 'All Vendors' }, ...(filterOptions.vendors || [])];

  // Filters state
  const [filters, setFilters] = useState({
    companyName: '', // Changed from null to string for TextField
    vendorId: null,
    // firstName: '', // Removed
    // lastName: '', // Removed
    contactName: '', // Added for Agent's Name
    contactEmail: '', // Added for Agent's Email
    effectiveDateStart: '',
    effectiveDateEnd: '',
    expirationDateStart: '',
    expirationDateEnd: '',
    country: '', // Kept as per existing structure, though not in specific update list for this phase
    city: '',    // Kept
    state: '',   // Kept
    zipCode: '', // Kept
    status: null,
    policyType: null,
    nearExpiration: false
  });
  // Latest filters for async callbacks that outlive the render they started in
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const rowsPerPageRef = useRef(rowsPerPage);
  rowsPerPageRef.current = rowsPerPage;
  // Only the newest options request may update the suggestions
  const optionsRequestRef = useRef(0);

  // Helper function to get auth headers
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Fetch and group insurance records
  const fetchInsuranceRecords = useCallback(async (pageNum = 0, rowsPerPageNum = 10, currentFilters = {}) => {
    setLoading(true);
    setError(null);
    // setGroupedPolicies(new Map()); // Clear only if filters significantly change affecting grouping

    try {
      const headers = getAuthHeader();
      if (!headers) return;

      const params = new URLSearchParams();
      params.append('page', pageNum + 1); // API uses 1-based indexing
      params.append('limit', rowsPerPageNum);

      if (currentFilters.vendorId) params.append('vendorId', currentFilters.vendorId);
      if (currentFilters.companyName) params.append('companyName', currentFilters.companyName);
      if (currentFilters.contactName) params.append('contactName', currentFilters.contactName);
      if (currentFilters.contactEmail) params.append('contactEmail', currentFilters.contactEmail);
      if (currentFilters.effectiveDateStart) params.append('effectiveDateStart', currentFilters.effectiveDateStart);
      if (currentFilters.effectiveDateEnd) params.append('effectiveDateEnd', currentFilters.effectiveDateEnd);
      if (currentFilters.expirationDateStart) params.append('expirationDateStart', currentFilters.expirationDateStart);
      if (currentFilters.expirationDateEnd) params.append('expirationDateEnd', currentFilters.expirationDateEnd);
      if (currentFilters.policyType) params.append('policyType', currentFilters.policyType);
      if (currentFilters.status) params.append('status', currentFilters.status);
      if (currentFilters.country) params.append('country', currentFilters.country);
      if (currentFilters.city) params.append('city', currentFilters.city);
      if (currentFilters.state) params.append('state', currentFilters.state);
      if (currentFilters.zipCode) params.append('zipCode', currentFilters.zipCode);
      // nearExpiration is client-side

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/insurance?${params.toString()}`, { headers });
      const records = response.data.records || []; // Assuming API returns { records: [], totalCount: X }
      setTotalCount(response.data.totalCount || 0);


      const grouped = new Map();
      records.forEach(record => {
        const vendorIdKey = record.vendorId || 'unknown_vendor';
        if (!grouped.has(vendorIdKey)) {
          grouped.set(vendorIdKey, []);
        }
        grouped.get(vendorIdKey).push(record);
      });
      setGroupedPolicies(grouped);

    } catch (err) {
      console.error('Error fetching insurance records:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load insurance records. Please try again.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      if (err.response && err.response.status === 401) window.location.href = '/login';
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  // Fetch filter options scoped to the selected vendor / provider. Resolves to
  // the options, or null when a newer request has since superseded this one.
  const fetchFilterOptions = useCallback(async (vendorId, companyName) => {
    const requestId = ++optionsRequestRef.current;
    try {
      const headers = getAuthHeader();
      if (!headers) return null;
      const params = new URLSearchParams();
      if (vendorId) params.append('vendorId', vendorId);
      if (companyName) params.append('companyName', companyName);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/insurance/filter-options?${params.toString()}`, { headers });
      if (requestId !== optionsRequestRef.current) return null;
      const options = { ...EMPTY_FILTER_OPTIONS, ...(response.data || {}) };
      setFilterOptions(options);
      return options;
    } catch (err) {
      console.error('Error fetching filter options:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load filter options. Please try again.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      return null;
    }
  }, [getAuthHeader]);

  // Initial data load
  useEffect(() => {
    fetchInsuranceRecords(page, rowsPerPage, filters);
  }, [fetchInsuranceRecords, page, rowsPerPage]); // Added page and rowsPerPage

  useEffect(() => {
    fetchFilterOptions(null, '');
  }, [fetchFilterOptions]);

  const applyFilters = (updatedFilters) => {
    filtersRef.current = updatedFilters;
    setFilters(updatedFilters);
    setPage(0); // Reset to first page when filters change
    fetchInsuranceRecords(0, rowsPerPageRef.current, updatedFilters);
  };

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    let filterValue = value;

    if (name === 'vendorId' && value?.companyName === 'All Vendors') {
      filterValue = null;
    } else if (name === 'vendorId' && value) {
      filterValue = value.vendorIdentifier;
    }

    const updatedFilters = {
      ...filters,
      [name]: filterValue
    };
    applyFilters(updatedFilters);

    // The suggestion lists narrow to the vendor / provider selection
    if (name === 'vendorId' || name === 'companyName') {
      fetchFilterOptions(updatedFilters.vendorId, updatedFilters.companyName).then((options) => {
        if (!options || name !== 'companyName') return;
        // A provider was chosen: drop a vendor that no longer carries it
        const current = filtersRef.current;
        if (!current.vendorId) return;
        const stillListed = (options.vendors || []).some((v) => v.vendorIdentifier === current.vendorId);
        if (!stillListed) {
          const cleared = { ...current, vendorId: null };
          applyFilters(cleared);
          fetchFilterOptions(null, cleared.companyName);
        }
      });
    }
  };

  // Free-text filter that also suggests the values present in the current selection
  const renderTextFilter = (name, label, options) => (
    <Autocomplete
      freeSolo
      options={options || []}
      inputValue={filters[name] || ''}
      onInputChange={(event, newValue, reason) => {
        if (reason === 'reset' && !newValue) return;
        handleFilterChange(name, newValue || '');
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      fullWidth
      disabled={loading}
    />
  );

  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchInsuranceRecords(newPage, rowsPerPage, filters);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchInsuranceRecords(0, newRowsPerPage, filters);
  };

  // Format date for display
  const formatDate = formatDateOrNA;

  // Format currency for display
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Expired': return 'error';
      case 'Pending': return 'warning';
      case 'Rejected': return 'error';
      case 'Needs Review': return 'info';
      default: return 'default';
    }
  };

  // Check if an insurance record is near expiration
  const isNearExpiration = (expirationDate) => {
    if (!expirationDate) return false;
    
    const today = new Date();
    const expiration = new Date(expirationDate);
    const ninetyDaysFromNow = new Date();
    ninetyDaysFromNow.setDate(today.getDate() + 90);
    
    return expiration <= ninetyDaysFromNow && expiration >= today;
  };

  // View insurance COI
  const handleViewCOI = (insurance) => {
    if (insurance.documentPath) {
      const url = getDocumentUrl(insurance.documentPath);
      setCoiToViewUrl(url);
      setViewCOIOpen(true);
    } else {
      const errorMessage = 'No document available to view.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      console.log('No document path for:', insurance);
    }
  };
  
  const handleViewCOIClose = () => {
    setViewCOIOpen(false);
    setCoiToViewUrl('');
  };

  // Download insurance document
  const handleDownloadDocument = (insurance) => {
    if (!insurance.documentPath) {
      const errorMessage = 'No document available for download';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      return;
    }
    const documentUrl = getDocumentUrl(insurance.documentPath);
    if (documentUrl) {
      window.open(documentUrl, '_blank');
    } else {
      const errorMessage = 'Could not construct document URL.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    }
  };

  // Edit insurance record
  const handleEditInsurance = (insurance) => {
    setSelectedInsurance(insurance);
    setEditDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedInsurance(null);
  };

  const handleDialogSave = async (updatedInsuranceData, insuranceId) => {
    setLoading(true); // Optional: reuse existing loading state or add a new one for dialog save
    setError(null);
    try {
      const headers = getAuthHeader(); // Ensure getAuthHeader is accessible or defined
      if (!headers) return;

      await axios.put(`${process.env.REACT_APP_API_URL}/api/insurance/${insuranceId}`, updatedInsuranceData, { headers });
      
      showNotification('Insurance record updated successfully', 'success');

      handleDialogClose();
      // Refresh the insurance records list to show the updated data
      fetchInsuranceRecords(page, rowsPerPage, filters); // Refetch data with current filters, page, and rowsPerPage
    } catch (err) {
      console.error('Error updating insurance record:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to update insurance record. Please try again.';
      setError(`Failed to update insurance record: ${errorMessage}`);
      showNotification(errorMessage, 'error');
      if (err.response && err.response.status === 401) window.location.href = '/login';
    } finally {
      setLoading(false);
    }
  };
  
  // --- Add Policy Handlers ---
  const handleAddPolicyOpen = (vendorId, vendorCompanyName) => {
    setCurrentVendorForAdd({ vendorIdentifier: vendorId, companyName: vendorCompanyName || '' });
    setAddDialogOpen(true);
  };

  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
    setCurrentVendorForAdd(null);
  };
  
  const handleAddDialogSave = async (newPolicyData, coiFile) => {
    setLoading(true); // Consider a separate loading state for add
    setError(null);
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      // Validate that we have a vendor context
      const currentVendorIdForAdd = currentVendorForAdd?.vendorIdentifier;
      if (!currentVendorIdForAdd) {
        setError('No vendor selected. Please close the dialog and try again.');
        return;
      }

      // Ensure vendorId is included in the payload
      const payload = { ...newPolicyData, vendorId: currentVendorIdForAdd, status: 'Active' };

      // Create FormData for file upload
      const formData = new FormData();
      
      // Append policy data as JSON string
      formData.append('policyData', JSON.stringify(payload));
      
      // Append file if provided
      if (coiFile) {
        formData.append('coiDocument', coiFile);
      }

      // Update headers for multipart/form-data
      const uploadHeaders = {
        ...headers,
        'Content-Type': 'multipart/form-data'
      };
  
      await axios.post(`${process.env.REACT_APP_API_URL}/api/insurance`, formData, { headers: uploadHeaders });
  
      showNotification('Insurance policy added successfully', 'success');
      handleAddDialogClose();
      // Refresh data to show the newly added policy
      fetchInsuranceRecords(page, rowsPerPage, filters);
      fetchFilterOptions(filters.vendorId, filters.companyName);
    } catch (err) {
      console.error('Error adding insurance record:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to add insurance record. Please try again.';
      setError(`Failed to add insurance record: ${errorMessage}`);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // --- Delete Policy Handlers ---
  const handleDeleteConfirmOpen = (insurance) => {
    setPolicyToDelete(insurance);
    setDeleteConfirmOpen(true);
  };
  
  const handleDeleteConfirmClose = () => {
    setPolicyToDelete(null);
    setDeleteConfirmOpen(false);
  };
  
  const handleDeleteConfirm = async () => {
    if (!policyToDelete) return;
    
    setIsDeleting(true); // Start loading indicator for delete
    setError(null);
    try {
      const headers = getAuthHeader();
      if (!headers) return;
  
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/insurance/${policyToDelete._id}`, { headers });
  
      showNotification('Insurance policy deleted successfully', 'success');
      handleDeleteConfirmClose();
      // Refresh data to remove the deleted policy
      fetchInsuranceRecords(page, rowsPerPage, filters);
      fetchFilterOptions(filters.vendorId, filters.companyName);
    } catch (err) {
      console.error('Error deleting insurance record:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to delete insurance record. Please try again.';
      setError(`Failed to delete insurance record: ${errorMessage}`);
      showNotification(errorMessage, 'error');
    } finally {
      setIsDeleting(false); // Stop loading indicator
    }
  };

  // --- Request Policy Handlers ---
  const handleRequestPolicyOpen = () => {
    setRequestPolicyDialogOpen(true);
  };
  
  const handleRequestPolicyClose = () => {
    setRequestPolicyDialogOpen(false);
    // Optionally refresh data after a policy request is sent
    fetchInsuranceRecords(page, rowsPerPage, filters);
  };

  // Check if user has permission to access this page
  if (!hasPermission('canManageInsurance')) { // Changed to canManageInsurance
    return (
      <Box mt={2}>
        <Paper elevation={3} sx={{ padding: 2 }}>
          <Typography variant="h6" color="error">
            You do not have permission to access this page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Paper elevation={3} sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Insurance Records</Typography>
          {hasPermission('canManageInsurance') && ( // Changed to canManageInsurance
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleRequestPolicyOpen}
              disabled={loading}
            >
              Request Insurance Policy
            </Button>
          )}
        </Box>
        <Divider sx={{ my: 2 }} />

        {/* Filter Section */}
        <Box sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
          <Typography variant="subtitle1" gutterBottom>
            Filter Insurance Records
          </Typography>
          <Grid container spacing={2} alignItems="flex-end">
            {/* Company Name Filter (Insurance Provider) */}
            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('companyName', 'Insurance Provider Name', filterOptions.providers)}</Grid>

            {/* Vendor ID Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={vendorOptions}
                getOptionLabel={(option) => option.vendorIdentifier ? `${option.vendorIdentifier} (${option.companyName || 'N/A'})` : (option.companyName === 'All Vendors' ? 'All Vendors' : '')}
                value={vendorOptions.find(opt => opt.vendorIdentifier === filters.vendorId) || null}
                onChange={(event, newValue) => handleFilterChange('vendorId', newValue)}
                isOptionEqualToValue={(option, value) => option.vendorIdentifier === value?.vendorIdentifier}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor ID/Number"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start"><SearchIcon /></InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                fullWidth
                disabled={loading}
              />
            </Grid>

            {/* Agent's Name Filter */}
            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('contactName', "Agent's Name", filterOptions.contactNames)}</Grid>

            {/* Agent's Email Filter */}
            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('contactEmail', "Agent's Email", filterOptions.contactEmails)}</Grid>
            
            {/* Effective Date Start Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Effective Date From"
                type="date"
                value={filters.effectiveDateStart}
                onChange={(e) => handleFilterChange('effectiveDateStart', e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
            </Grid>

            {/* Effective Date End Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Effective Date To"
                type="date"
                value={filters.effectiveDateEnd}
                onChange={(e) => handleFilterChange('effectiveDateEnd', e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
            </Grid>

            {/* Expiration Date Start Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Expiration Date From"
                type="date"
                value={filters.expirationDateStart}
                onChange={(e) => handleFilterChange('expirationDateStart', e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
            </Grid>

            {/* Expiration Date End Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Expiration Date To"
                type="date"
                value={filters.expirationDateEnd}
                onChange={(e) => handleFilterChange('expirationDateEnd', e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
            </Grid>

            {/* Address Filters */}
            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('country', 'Country', filterOptions.countries)}</Grid>
            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('city', 'City', filterOptions.cities)}</Grid>
            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('state', 'State', filterOptions.states)}</Grid>
            <Grid item xs={12} sm={6} md={3}>{renderTextFilter('zipCode', 'Zip Code', filterOptions.zipCodes)}</Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={statusOptions}
                getOptionLabel={(option) => option || ''}
                value={filters.status || null}
                onChange={(event, newValue) => handleFilterChange('status', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Status"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start"><FilterIcon /></InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                fullWidth
                disabled={loading}
              />
            </Grid>
            
            {/* Policy Type Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={policyTypeOptions}
                getOptionLabel={(option) => option || ''}
                value={filters.policyType || null}
                onChange={(event, newValue) => handleFilterChange('policyType', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Policy Type"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start"><FilterIcon /></InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                fullWidth
                disabled={loading}
              />
            </Grid>

            {/* Near Expiration Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.nearExpiration}
                    onChange={(e) => handleFilterChange('nearExpiration', e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Near Expiration (90 days)"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Error Display */}
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Grouped Insurance Records Display */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box mt={3}>
            {Array.from(groupedPolicies.entries()).map(([vendorId, policies]) => {
              // The list endpoint joins the vendor's company name onto each record
              const vendorCompanyName = policies.find((p) => p.vendorCompanyName)?.vendorCompanyName || '';
              const vendorDisplayName = vendorCompanyName ? `${vendorCompanyName} (Vendor ID: ${vendorId})` : `Vendor ID: ${vendorId}`;
              
              // Client-side filtering for 'nearExpiration' as other filters are handled by backend
              const clientFilteredPolicies = policies.filter(policy => {
                const expirationMatch = !filters.nearExpiration || isNearExpiration(policy.expirationDate);
                // Any other purely client-side filters can go here.
                return expirationMatch;
              });

              // Determine if any server-side filters are active to adjust "no records" message
              const hasActiveServerFilters = filters.companyName || filters.contactName || filters.contactEmail ||
                                           filters.effectiveDateStart || filters.effectiveDateEnd ||
                                           filters.expirationDateStart || filters.expirationDateEnd ||
                                           filters.country || filters.city || filters.state || filters.zipCode ||
                                           filters.policyType || filters.status || filters.vendorId;

              if (clientFilteredPolicies.length === 0 && (hasActiveServerFilters || filters.nearExpiration)) {
                 // If vendorId is set, we show the vendor header even if no policies match
                 // If vendorId is not set (global search) and this vendor has no matches, skip rendering this vendor section
                 if (!filters.vendorId) return null;
              }
              
              // If filters.vendorId is set, or if it's not set (global search) and there are policies after client filtering, render the section.
              // Or if no filters are active at all, and there are policies.
              const shouldRenderVendorSection = filters.vendorId || clientFilteredPolicies.length > 0 || !hasActiveServerFilters;


              if (!shouldRenderVendorSection && clientFilteredPolicies.length === 0) {
                return null;
              }


              return (
                <Paper key={vendorId} elevation={2} sx={{ mb: 3, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">{vendorDisplayName}</Typography>
                    {hasPermission('canManageInsurance') && ( // Changed to canManageInsurance
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddPolicyOpen(vendorId, vendorCompanyName)}
                        title={`Add new policy for ${vendorCompanyName || vendorId}`}
                      >
                        Add Policy for This Vendor
                      </Button>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  {clientFilteredPolicies.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', fontStyle: 'italic', color: 'text.secondary' }}>
                      No insurance records found for this vendor{(hasActiveServerFilters || filters.nearExpiration) ? ' matching the current filters' : ''}.
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Policy Type</TableCell>
                            <TableCell>Provider</TableCell>
                            <TableCell>Policy Number</TableCell>
                            <TableCell>Agent's Name</TableCell>
                            <TableCell>Agent's Email</TableCell>
                            <TableCell>Coverage Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Effective Date</TableCell>
                            <TableCell>Expiration Date</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clientFilteredPolicies.map((insurance) => (
                            <TableRow key={insurance._id}>
                              <TableCell>{insurance.policyType || 'N/A'}</TableCell>
                              <TableCell>{insurance.companyName || 'N/A'}</TableCell>
                              <TableCell>{insurance.policyNumber || 'N/A'}</TableCell>
                              <TableCell>{insurance.contactName || 'N/A'}</TableCell>
                              <TableCell>{insurance.contactEmail || 'N/A'}</TableCell>
                              <TableCell>{formatCurrency(insurance.coverageAmount)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={insurance.status || 'Unknown'}
                                  color={getStatusColor(insurance.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{formatDate(insurance.effectiveDate)}</TableCell>
                              <TableCell>
                                {formatDate(insurance.expirationDate)}
                                {isNearExpiration(insurance.expirationDate) && (
                                  <Chip
                                    label="Expiring Soon"
                                    color="warning"
                                    size="small"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Tooltip title="View COI">
                                  <IconButton size="small" onClick={() => handleViewCOI(insurance)}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {insurance.documentPath && (
                                  <Tooltip title="Download Document">
                                    <IconButton size="small" onClick={() => handleDownloadDocument(insurance)}>
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {hasPermission('canManageInsurance') && ( // Changed to canManageInsurance
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => handleEditInsurance(insurance)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {hasPermission('canManageInsurance') && ( // Changed to canManageInsurance
                                  <Tooltip title="Delete">
                                    <IconButton size="small" onClick={() => handleDeleteConfirmOpen(insurance)} color="error">
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              );
            })}
            {groupedPolicies.size === 0 && !loading && (
               <Typography sx={{ textAlign: 'center', fontStyle: 'italic', color: 'text.secondary', mt: 4 }}>
                 No insurance records found{filters.vendorId ? ` for vendor ${filters.vendorId}` : (Object.values(filters).some(f => f) ? ' matching the current filters' : '')}.
               </Typography>
            )}
          </Box>
        )}
       {/* Pagination - Placed outside the loading check for grouped policies, but inside the main Paper */}
       {!loading && groupedPolicies.size > 0 && (
           <TablePagination
             component="div"
             count={totalCount}
             page={page}
             onPageChange={handleChangePage}
             rowsPerPage={rowsPerPage}
             onRowsPerPageChange={handleChangeRowsPerPage}
             rowsPerPageOptions={[5, 10, 25, 50, 100]} // Standard options
           />
       )}
      </Paper>

      {/* Edit Dialog */}
      {selectedInsurance && (
        <EditInsuranceDialog
          open={editDialogOpen}
          insurance={selectedInsurance}
          onClose={handleDialogClose}
          onSave={handleDialogSave}
          policyTypeOptions={DEFAULT_POLICY_TYPES}
        />
      )}

      {/* Add Dialog */}
      <PolicyFormDialog
        open={addDialogOpen}
        onClose={handleAddDialogClose}
        onSave={handleAddDialogSave}
        vendorId={currentVendorForAdd?.vendorIdentifier || null}
        vendorInfo={currentVendorForAdd}
        policyTypes={DEFAULT_POLICY_TYPES}
      />
      
      {/* View COI Modal */}
      <ViewCOIModal
        open={viewCOIOpen}
        onClose={handleViewCOIClose}
        documentUrl={coiToViewUrl}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onClose={handleDeleteConfirmClose}
        onConfirm={handleDeleteConfirm}
        title="Confirm Deletion"
        contentText={`Are you sure you want to delete the policy "${policyToDelete?.policyNumber || 'this policy'}" (${policyToDelete?.policyType || 'N/A'}) for ${policyToDelete?.companyName || 'N/A'} and all its associated documents? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        loading={isDeleting} // Pass loading state
      />

      {/* Request Insurance Policy Dialog */}
      <RequestInsurancePolicyDialog
        open={requestPolicyDialogOpen}
        onClose={handleRequestPolicyClose}
      />
    </Box>
  );
}

export default InsuranceSettings;